import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Paperclip, Upload, FileText, ImageIcon, Trash2, ArrowUpRight, Sparkles, ShieldAlert, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MAX_DOCS = 5;
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT_TYPES = "application/pdf,image/jpeg,image/jpg,image/png";

function fileSize(b) {
  if (!b) return "0 B";
  const kb = b / 1024;
  return kb < 1024 ? `${kb.toFixed(0)} KB` : `${(kb / 1024).toFixed(1)} MB`;
}

export function ProofDocsButton({ item, onChange }) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(item?.doc_count || 0);
  const label = count > 0 ? `${count} proof · attach more` : "Attach proof";
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid={`proof-docs-btn-${item.id}`}
        className={`rounded-full h-8 px-3 text-xs ${count > 0 ? "text-[#D5FF3F] hover:text-[#D5FF3F]" : "text-[#A8ADA5] hover:text-[#F5F3EE]"}`}
      >
        <Paperclip size={13} className="mr-1.5" />
        {label}
      </Button>
      <ProofDocsDrawer
        open={open}
        onOpenChange={setOpen}
        item={item}
        onCountChange={(n) => {
          setCount(n);
          if (onChange) onChange(n);
        }}
      />
    </>
  );
}

function ProofDocsDrawer({ open, onOpenChange, item, onCountChange }) {
  const { user } = useAuth();
  const plan = user?.plan || "free";
  const paid = plan === "basic" || plan === "plus";
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [description, setDescription] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open || !item?.id) return;
    load();
     
  }, [open, item?.id]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/items/${item.id}/documents`);
      setDocs(data.documents || []);
      if (onCountChange) onCountChange((data.documents || []).length);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error("File too large — max 10 MB.");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    if (description) form.append("description", description);
    setUploading(true);
    try {
      const { data } = await api.post(`/items/${item.id}/documents/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Proof attached — will mail with your letter");
      setDocs((cur) => {
        const next = [...cur, data];
        if (onCountChange) onCountChange(next.length);
        return next;
      });
      setDescription("");
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.info("Attaching proof requires Basic or Plus.");
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeDoc = async (docId) => {
    if (!window.confirm("Remove this proof document?")) return;
    try {
      await api.delete(`/items/${item.id}/documents/${docId}`);
      setDocs((cur) => {
        const next = cur.filter((d) => d.id !== docId);
        if (onCountChange) onCountChange(next.length);
        return next;
      });
      toast.success("Removed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const atLimit = docs.length >= MAX_DOCS;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-[#131518] border-l border-[#26282C] text-[#F5F3EE] overflow-y-auto"
        data-testid="proof-docs-drawer"
      >
        <SheetHeader>
          <SheetTitle className="font-display text-2xl text-[#F5F3EE]">Attach proof</SheetTitle>
          <SheetDescription className="text-[#A8ADA5]">
            Upload receipts, court docs, ID copies, or your FTC Identity Theft Report. Every proof
            mails with your dispute letter as a labeled exhibit.
          </SheetDescription>
        </SheetHeader>

        {/* Trust-signal disclaimer — appears BEFORE upload zone or paywall so the user knows
            why this matters. Attaching evidence is often the difference between deletion and
            a "verified as accurate" bounce-back. */}
        <div
          className="mt-5 rounded-sm border border-[#7CFF9B]/30 bg-[#7CFF9B]/8 p-4"
          data-testid="proof-evidence-disclaimer"
        >
          <div className="flex items-start gap-3">
            <ShieldAlert size={18} className="text-[#7CFF9B] flex-shrink-0 mt-0.5" />
            <div className="text-xs leading-relaxed">
              <div className="text-[#F5F3EE] font-medium mb-1">
                Evidence dramatically increases the chance your dispute goes through.
              </div>
              <div className="text-[#A8ADA5]">
                Letters mailed <span className="text-[#F5F3EE]">with supporting proof</span> get
                resolved far more often than bare claims. Without an exhibit, bureaus often reply
                &ldquo;verified as accurate&rdquo; and the item stays. Attach anything you can: a
                paid-in-full receipt, a court order, a signed statement, or&mdash;for identity
                theft&mdash;your FTC Identity Theft Report.
              </div>
            </div>
          </div>
        </div>

        {/* FTC Identity Theft Report — first-class evidence for identity_theft claims.
            Under 15 U.S.C. §1681c-2, a filed FTC ID Theft Report legally REQUIRES bureaus
            to block the fraudulent item within 4 business days. Always show the link, but
            highlight it prominently when the item's strategy suggests identity theft. */}
        {(item?.recommended_strategy === "identity_theft" || item?.item_type === "identity_theft") && (
          <div
            className="mt-3 rounded-sm border border-[#D5FF3F]/40 bg-[#D5FF3F]/10 p-4"
            data-testid="ftc-report-callout"
          >
            <div className="flex items-start gap-3">
              <Sparkles size={16} className="text-[#D5FF3F] flex-shrink-0 mt-0.5" />
              <div className="text-xs leading-relaxed flex-1">
                <div className="text-[#D5FF3F] font-medium mb-1 uppercase tracking-widest text-[10px] font-mono">
                  Strongest ammo for this claim
                </div>
                <div className="text-[#F5F3EE] font-medium mb-1">
                  Attach your FTC Identity Theft Report
                </div>
                <div className="text-[#A8ADA5] mb-2">
                  Under 15 U.S.C. § 1681c-2, a filed FTC report legally requires the bureau to
                  block the fraudulent item within <span className="text-[#F5F3EE]">4 business days</span>.
                  Filing at identitytheft.gov is free and takes about 10 minutes.
                </div>
                <a
                  href="https://www.identitytheft.gov/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#D5FF3F] hover:underline"
                  data-testid="ftc-report-link"
                >
                  File at identitytheft.gov <ExternalLink size={11} />
                </a>
              </div>
            </div>
          </div>
        )}

        {!paid ? (
          <div className="mt-6 card-surface p-5">
            <div className="flex items-start gap-3 mb-4">
              <Sparkles size={18} className="text-[#D5FF3F] flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="text-[#F5F3EE] font-medium mb-1">Proof docs are a paid feature.</div>
                <div className="text-[#A8ADA5]">
                  Unlock with Basic or Plus. Every attachment mails alongside your dispute letter as
                  a real exhibit (Exhibit A, B, C…).
                </div>
              </div>
            </div>
            <Link to="/app/pricing" onClick={() => onOpenChange(false)}>
              <Button className="w-full rounded-full btn-lime h-11" data-testid="proof-docs-upgrade-btn">
                See packages <ArrowUpRight size={14} className="ml-1.5" />
              </Button>
            </Link>
          </div>
        ) : (
          <>
            {/* Upload zone */}
            <div className="mt-6">
              <input
                ref={inputRef}
                type="file"
                accept={ACCEPT_TYPES}
                className="hidden"
                data-testid="proof-file-input"
                onChange={(e) => uploadFile(e.target.files?.[0])}
              />
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={uploading || atLimit}
                data-testid="proof-upload-btn"
                className={`w-full rounded-lg border-2 border-dashed p-6 text-center transition-all ${
                  atLimit
                    ? "border-[#26282C] bg-[#0B0C0E] text-[#6D716C] cursor-not-allowed"
                    : uploading
                    ? "border-[#D5FF3F]/50 bg-[#D5FF3F]/5"
                    : "border-[#26282C] bg-[#0B0C0E] hover:border-[#D5FF3F]/60 hover:bg-[#D5FF3F]/5"
                }`}
              >
                <Upload
                  size={22}
                  className={`mx-auto mb-2 ${uploading ? "animate-pulse text-[#D5FF3F]" : atLimit ? "text-[#6D716C]" : "text-[#D5FF3F]"}`}
                />
                <div className={`text-sm font-medium ${atLimit ? "text-[#6D716C]" : "text-[#F5F3EE]"}`}>
                  {uploading ? "Uploading…" : atLimit ? "Max 5 docs — remove one to add more" : "Tap to pick a PDF or image"}
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#6D716C] mt-1">
                  PDF · JPG · PNG · up to 10 MB
                </div>
              </button>

              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional: describe this proof (e.g., 'Paid-in-full receipt')"
                data-testid="proof-description-input"
                className="mt-3 w-full bg-[#0B0C0E] border border-[#26282C] rounded-sm px-3 py-2 text-sm text-[#F5F3EE] placeholder:text-[#6D716C] focus:border-[#D5FF3F]/50 outline-none"
              />
            </div>

            {/* Attached docs list */}
            <div className="mt-8">
              <div className="overline mb-3">
                Attached ({docs.length}/{MAX_DOCS})
              </div>
              {loading ? (
                <div className="text-sm text-[#A8ADA5] font-mono">Loading…</div>
              ) : docs.length === 0 ? (
                <div className="text-sm text-[#6D716C] font-mono">
                  No proof attached yet. Anything you add here mails with the letter.
                </div>
              ) : (
                <ul className="space-y-2" data-testid="proof-docs-list">
                  {docs.map((d, i) => {
                    const isPdf = (d.content_type || "").includes("pdf");
                    const Icon = isPdf ? FileText : ImageIcon;
                    const label = String.fromCharCode(65 + i);
                    return (
                      <li
                        key={d.id}
                        className="flex items-start gap-3 card-surface p-3"
                        data-testid={`proof-doc-${d.id}`}
                      >
                        <div className="w-9 h-9 rounded-sm bg-[#D5FF3F]/10 border border-[#D5FF3F]/25 flex items-center justify-center flex-shrink-0">
                          <Icon size={15} className="text-[#D5FF3F]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-[#F5F3EE] truncate">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-[#D5FF3F] mr-1.5">
                              Exhibit {label}
                            </span>
                            {d.original_filename}
                          </div>
                          {d.description && (
                            <div className="text-xs text-[#A8ADA5] mt-0.5 truncate">{d.description}</div>
                          )}
                          <div className="text-[10px] font-mono uppercase tracking-widest text-[#6D716C] mt-0.5">
                            {fileSize(d.size)} · {d.content_type?.split("/")[1] || "file"}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeDoc(d.id)}
                          className="text-[#6D716C] hover:text-[#FF6B4A] p-1"
                          data-testid={`proof-doc-remove-${d.id}`}
                          aria-label="Remove"
                        >
                          <Trash2 size={14} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="mt-8 text-[10px] font-mono uppercase tracking-widest text-[#6D716C] text-center">
              Bureaus routinely accept exhibits with FCRA disputes · Kept private, encrypted at rest
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
